<template>
  <b-card title="General Settings">
    <b-overlay
      :show="loading"
      blur="10px"
      opacity="0.30"
      rounded="sm"
      variant="light"
    >
      <b-form
        @submit.prevent
      >
        <b-row>
          <b-card
            title="Application Zoom"
            class="mb-0 mt-0 w-100"
          ><b-row>
            <b-col
              cols="9"
              md="3"
            >
              <b-form-input
                id="browser_zoom"
                v-model="zoom"
                type="range"
                min="65"
                max="100"
                @input="onZoomChange"
              />
            </b-col>
            <b-col cols="3">
              <div class="text-primary font-weight-bolder">
                {{ `${zoom}%` }}
              </div>
            </b-col>
          </b-row>
          </b-card>
        </b-row>
        <b-row>
          <div class="ml-2">
            This slider controls the zoom level of your application content. Decrease for a more broader view.
          </div>
        </b-row>
        <!-- Multilingual Settings Section -->
        <b-row class="mt-4">
          <b-card
            title="Language Management"
            class="mb-0 mt-0 w-100"
          >
            <!-- City Toggle Section -->
            <div class="mb-4">
              <div class="d-flex align-items-center justify-content-between mb-2">
                <label class="form-label mb-0">
                  City Multilingual Support
                  <feather-icon
                    v-b-tooltip.hover.v-primary="'Enable multilingual support for city names'"
                    icon="HelpCircleIcon"
                    size="15"
                    class="cursor-pointer text-primary mb-1 ml-1 mt-1"
                  />
                </label>
                <b-form-checkbox
                  v-model="cityMultilingualEnabled"
                  switch
                  @change="onCityMultilingualToggle"
                />
              </div>
            </div>
            <!-- Configured Languages -->
            <div
              v-if="cityMultilingualEnabled"
              class="mb-4"
            >
              <div class="d-flex align-items-center justify-content-between mb-3">
                <h6 class="mb-0">Configured Languages
                </h6>
                <b-badge variant="light-secondary">
                  {{ cityLanguages.length }} Languages
                </b-badge>
              </div>
              <!-- Language List -->
              <div class="language-list">
                <div
                  v-for="(language, index) in cityLanguages"
                  :key="language.code"
                  class="language-item d-flex align-items-center justify-content-between p-3 mb-2 border rounded"
                >
                  <div class="d-flex align-items-center">
                    <div class="language-badge bg-primary text-white rounded-circle d-flex align-items-center justify-content-center mr-3">
                      {{ language.code.toLowerCase() }}
                    </div>
                    <div>
                      <div class="font-weight-bold">{{ language.name }}
                      </div>
                      <small class="text-muted">Code: {{ language.code.toLowerCase() }}</small>
                    </div>
                    <b-badge
                      v-if="language.is_default"
                      v-b-tooltip.hover.v-primary="'Set default - all dropdowns display this language'"
                      variant="primary"
                      class="ml-2"
                    >
                      Default
                    </b-badge>
                  </div>
                  <div class="d-flex align-items-center">
                    <b-button
                      v-if="!language.is_default"
                      variant="outline-primary"
                      size="sm"
                      class="mr-2"
                      @click="makeDefault(index)"
                    >
                      Make Default
                    </b-button>
                    <feather-icon
                      icon="TrashIcon"
                      size="16"
                      class="text-muted cursor-pointer"
                      @click="deleteLanguage(index)"
                    />
                  </div>
                </div>
              </div>
            </div>
            <!-- Add New Language Section -->
            <div
              v-if="cityMultilingualEnabled"
              class="add-language-section"
            >
              <h6 class="mb-3">Add New Language
              </h6>
              <b-row>
                <b-col md="6">
                  <b-form-group label="Language Name">
                    <b-form-input
                      v-model="newLanguage.name"
                      placeholder="e.g., French"
                    />
                  </b-form-group>
                </b-col>
                <b-col md="6">
                  <b-form-group label="Language Code">
                    <b-form-input
                      v-model="newLanguage.code"
                      placeholder="e.g., fr"
                      :maxlength="5"
                      :state="languageCodeState"
                      @input="onLanguageCodeInput"
                    />
                    <b-form-invalid-feedback v-if="languageCodeError">
                      {{ languageCodeError }}
                    </b-form-invalid-feedback>
                    <b-form-text>
                      Maximum 5 characters allowed
                    </b-form-text>
                  </b-form-group>
                </b-col>
              </b-row>
              <b-row>
                <b-col md="12">
                  <b-form-checkbox
                    v-model="newLanguage.is_default"
                    class="mb-3"
                  >
                    Set as default language
                  </b-form-checkbox>
                </b-col>
              </b-row>
              <b-button
                variant="primary"
                class="w-100"
                @click="addLanguage"
              >
                <feather-icon
                  icon="PlusIcon"
                  size="16"
                  class="mr-1"
                />
                Add Language
              </b-button>
            </div>
          </b-card>
        </b-row>
      </b-form>
    </b-overlay>
  </b-card>
</template>

<script>
import {
  BRow, BCol, BForm, BFormInput, BCard, BOverlay, BFormGroup, BFormCheckbox, BButton, BBadge, VBTooltip,
} from 'bootstrap-vue'
import Ripple from 'vue-ripple-directive'
import SuccessMessage from '@core/mixins/SuccessMessage'
import ErrorMessage from '@core/mixins/ErrorMessage'
import { RepositoryFactory } from '@/repository/RepositoryFactory'

const MultilingualSettingsRepository = RepositoryFactory.get('multilingualSettings')

export default {
  components: {
    BRow,
    BCol,
    BForm,
    BFormInput,
    BCard,
    BOverlay,
    BFormGroup,
    BFormCheckbox,
    BButton,
    BBadge,
  },
  directives: {
    Ripple,
    'b-tooltip': VBTooltip,
  },
  mixins: [SuccessMessage, ErrorMessage],
  data() {
    return {
      loading: false,
      zoom: 100,
      cityMultilingualEnabled: false,
      cityLanguages: [],
      newLanguage: {
        name: '',
        code: '',
        is_default: false,
      },
      languageCodeState: null,
      languageCodeError: '',
    }
  },
  mounted() {
    this.zoom = localStorage.getItem('browser_zoom') && localStorage.getItem('browser_zoom') !== 'null' ? Number(localStorage.getItem('browser_zoom')) : 100
    this.fetchMultilingualSettings()
  },
  methods: {
    onZoomChange(e) {
      window.parent.document.body.style.zoom = e / 100
      localStorage.setItem('browser_zoom', e)
    },
    async fetchMultilingualSettings() {
      try {
        this.loading = true
        const response = await MultilingualSettingsRepository.getMultilingualSettings('city')
        if (response && response.data && response.data.data) {
          const { data } = response.data
          // Check if this is the city section response
          if (data.section === 'city') {
            this.cityMultilingualEnabled = data.enable || false
            this.cityLanguages = data.languages || []
          } else {
            this.cityMultilingualEnabled = false
            this.cityLanguages = []
          }
        } else {
          this.cityMultilingualEnabled = false
          this.cityLanguages = []
        }
      } catch (e) {
        this.convertAndNotifyError(e)
        // Set defaults on error
        this.cityMultilingualEnabled = false
        this.cityLanguages = []
      } finally {
        this.loading = false
      }
    },
    async onCityMultilingualToggle() {
      try {
        this.loading = true
        const payload = {
          enable: this.cityMultilingualEnabled,
          languages: this.cityLanguages,
        }
        await MultilingualSettingsRepository.updateMultilingualSettings('city', payload)
        // Clear city-related localStorage caches when language settings change
        localStorage.removeItem('city_list')
        localStorage.removeItem('destination_city_list')
        localStorage.removeItem('city_list_with_warehouse')
        this.showSuccessMessage('Multilingual settings updated successfully')
      } catch (e) {
        this.convertAndNotifyError(e)
        // Revert toggle on error
        this.cityMultilingualEnabled = !this.cityMultilingualEnabled
      } finally {
        this.loading = false
      }
    },
    validateLanguageCode() {
      const { code } = this.newLanguage
      if (code.length > 5) {
        this.languageCodeState = false
        this.languageCodeError = 'Language code cannot exceed 5 characters'
        return false
      }
      if (code.length === 0) {
        this.languageCodeState = null
        this.languageCodeError = ''
        return true
      }
      // Check if language code already exists
      if (this.cityLanguages.some(lang => lang.code.toLowerCase() === code.toLowerCase())) {
        this.languageCodeState = false
        this.languageCodeError = 'Language code already exists'
        return false
      }
      this.languageCodeState = true
      this.languageCodeError = ''
      return true
    },
    onLanguageCodeInput() {
      // Convert to lowercase as user types
      this.newLanguage.code = this.newLanguage.code.toLowerCase()
      this.validateLanguageCode()
    },
    addLanguage() {
      // Validate language code first (before other checks)
      if (!this.validateLanguageCode()) {
        return
      }
      if (!this.newLanguage.name || !this.newLanguage.code) {
        this.showErrorMessage('Please enter both language name and code')
        return
      }
      // Check if language code already exists (additional check)
      if (this.cityLanguages.some(lang => lang.code.toLowerCase() === this.newLanguage.code.toLowerCase())) {
        this.showErrorMessage('Language code already exists')
        return
      }
      // If setting as default, remove default from other languages
      if (this.newLanguage.is_default) {
        this.cityLanguages = this.cityLanguages.map(lang => ({
          ...lang,
          is_default: false,
        }))
      }
      this.cityLanguages.push({
        ...this.newLanguage,
        code: this.newLanguage.code.toLowerCase(),
      })
      this.newLanguage = { name: '', code: '', is_default: false }
      this.languageCodeState = null
      this.languageCodeError = ''
      this.saveMultilingualSettings()
    },
    makeDefault(index) {
      // Remove default from all languages first
      this.cityLanguages = this.cityLanguages.map(lang => ({
        ...lang,
        is_default: false,
      }))
      // Set the selected language as default
      this.cityLanguages[index].is_default = true
      // Save the changes
      this.saveMultilingualSettings()
    },
    deleteLanguage(index) {
      this.cityLanguages.splice(index, 1)
      this.saveMultilingualSettings()
    },
    async saveMultilingualSettings() {
      try {
        this.loading = true
        const payload = {
          enable: this.cityMultilingualEnabled,
          languages: this.cityLanguages,
        }
        await MultilingualSettingsRepository.updateMultilingualSettings('city', payload)
        // Clear city-related localStorage caches when language settings change
        localStorage.removeItem('city_list')
        localStorage.removeItem('destination_city_list')
        localStorage.removeItem('city_list_with_warehouse')
        this.showSuccessMessage('Language settings updated successfully')
      } catch (e) {
        this.convertAndNotifyError(e)
      } finally {
        this.loading = false
      }
    },
  },
}
</script>

<style scoped>
.language-badge {
  width: 32px;
  height: 32px;
  font-size: 12px;
  font-weight: bold;
}

.language-item {
  background-color: #fff;
  border: 1px solid #e0e0e0 !important;
  transition: all 0.2s ease;
}

.language-item:hover {
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.cursor-pointer {
  cursor: pointer;
}

.cursor-pointer:hover {
  color: #007bff !important;
}

.add-language-section {
  border-top: 1px solid #e0e0e0;
  padding-top: 1rem;
}

.cursor-help {
  cursor: help;
}
</style>
